import { CommerceService } from "@/services/commerce"
import { EinzaGraphiQLService } from "@/services/einzaGraphiQL"

export const admBonusAgreements = async ({ admNumber, year }) => {
  const parsedYear = Number(year)
  const yearToUse = parsedYear > 0 ? parsedYear : new Date().getFullYear()

  const variables = { admNumber }
  const fields = ["customerList", "admNumber", "customerCount"]

  const graphiQLQueryObject = [
    {
      method: "findBonusAgreementsSummaryByAdm",
      variables: variables,
      fields,
    },
  ]
  const mappedQuery = EinzaGraphiQLService.formQuery(graphiQLQueryObject)

  let queryResponse = (await EinzaGraphiQLService.getData(mappedQuery))?.data

  if (!queryResponse) throw new Error("Data retrieval for adm customers with bonus agreements failed")

  const customerList = queryResponse?.findBonusAgreementsSummaryByAdm?.customerList

  const customersWithNames = await getCustomerName(customerList)

  const customerAgreements = await getCustomerAgreementsByYear(customerList, yearToUse)

  const customerRevenue = await getCustomerRevenue(customerList, yearToUse)

  const result = combineData(customersWithNames, customerAgreements, customerRevenue)

  return { admBonus: result || null }
}

async function getCustomerAgreementsByYear(customerList, year) {
  if (!customerList || customerList.length === 0) {
    return null
  }

  const yearStart = Number(`${year}01`)
  const yearEnd = Number(`${year}12`)

  const queryPromises = customerList.map(async (customerNumber) => {
    const variables = {
      customerNumber,
      page: 0,
      size: 100,
      sort: "dateFromYYYYMM.asc",
      filters: [{ field: "year", operator: "eq", value: year.toString() }],
    }

    const queryObject = [
      {
        method: "findBonusAgreementsByCustomerAndFilter",
        variables,
        fields: ["totalElements"],
        expandedFields: {
          content: [
            "bonusId",
            "type",
            "articleMainGroup",
            "articleGroup",
            "salesLevel1",
            "bonusPercentLevel1",
            "salesLevel2",
            "bonusPercentLevel2",
            "salesLevel3",
            "bonusPercentLevel3",
            "dateFromYYYYMM",
            "dateTillYYYYMM",
            "customerNumber",
          ],
        },
      },
    ]

    const mappedQuery = EinzaGraphiQLService.formQuery(queryObject)

    const response = (await EinzaGraphiQLService.getData(mappedQuery))?.data

    if (!response) {
      return []
    }

    const customerBonuses = response?.findBonusAgreementsByCustomerAndFilter?.content || []

    return customerBonuses.filter((el) => {
      const startsBeforeYearEnd = Number(el?.dateFromYYYYMM) <= yearEnd
      const endsAfterYearStart = el?.dateTillYYYYMM === null || Number(el?.dateTillYYYYMM) >= yearStart
      return startsBeforeYearEnd && endsAfterYearStart
    })
  })

  const allResults = await Promise.all(queryPromises)
  const result = allResults.flat()

  return result
}

async function getCustomerName(customerList) {
  if (customerList === null || customerList?.length === 0) {
    return null
  }

  const commerceApi = CommerceService.api()

  // add quotation marks for commerce tools
  const customerNumbers = customerList.map((el) => `"${el}" `)
  const whereClause = `(customerNumber in (${customerNumbers}))`

  const response = await commerceApi
    .customers()
    .get({
      queryArgs: {
        where: whereClause,
        expand: [],
      },
    })
    .execute()

  const result =
    response?.body?.results?.reduce((map, customer) => {
      map[customer.customerNumber] = {
        companyName: customer?.companyName,
        customerId: customer?.id,
        customerBranch: customer?.custom?.fields?.customerBranch,
      }
      return map
    }, {}) || null

  return result
}

async function getCustomerRevenue(customerList, year) {
  if (customerList === null || customerList?.length === 0 || !year) {
    return null
  }

  const queryPromises = customerList.map(async (customerNumber) => {
    const queryObject = [
      {
        method: "customerStatsByArticleMainGroup",
        variables: { customerNumber, year },
        fields: ["revenue", "margin"],
      },
    ]

    const mappedQuery = EinzaGraphiQLService.formQuery(queryObject)

    const response = (await EinzaGraphiQLService.getData(mappedQuery))?.data

    if (!response) {
      return { customerNumber, revenue: 0, margin: 0 }
    }

    const revenueData = response?.customerStatsByArticleMainGroup || []
    const revenue = revenueData.reduce((acc, { revenue }) => acc + revenue, 0) || 0
    const margin = revenueData.reduce((acc, { margin }) => acc + margin, 0) || 0

    return { customerNumber, revenue, margin }
  })

  const allResults = await Promise.all(queryPromises)

  // Convert array to object keyed by customerNumber
  const result = allResults.reduce((acc, { customerNumber, revenue, margin }) => {
    acc[customerNumber] = { revenue, margin }
    return acc
  }, {})

  return result
}

function combineData(customersWithName, customerAgreements, customerRevenue) {
  return customerAgreements.map((customer) => {
    return {
      ...customersWithName[customer?.customerNumber],
      ...customerRevenue[customer?.customerNumber],
      ...customer,
    }
  })
}
